/* eslint-disable no-continue */

/* eslint-disable no-restricted-syntax */
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useMediaQuery,
} from '@chakra-ui/react';
import Big from 'big.js';
import { startOfToday } from 'date-fns/esm';
import { toast } from 'react-toastify';

import { Deal, DealStateEnum, titlingDashboardDealsQuery } from '../gql/dealGql';
import {
  FollowUpStatusEnum,
  LocationType,
  Query,
  SortDirection,
  TeamType,
  useDashboardDealsQuery,
  useLaneKeyMetricsReaderQuery,
  useOnDealUpdateSubscription,
  useOnTagsCreatedSubscription,
  useOnTagsDeletedSubscription,
} from '../gql/generated/graphql';
import { LaneKeyMetrics } from '../gql/laneKeyMetrics';

import LeaseEndContainer from '../components/Container/LEContainer';
import BottomSection from '../components/Dashboards/BottomSection';
import ButtonsRow from '../components/Dashboards/ButtonsRow';
import DealsSwimlane from '../components/DealsSwimlane/DealsSwimlane';
import PayoffDealsTable from '../components/PayoffDealsTable';
import TempTagsQueue from '../components/Titling/TempTagsQueue/TempTagsQueue';
import TitleTable from '../components/Titling/TitleTracker/TitleTable';
import {
  SortingActionKind,
  FiltersActionKind as TitlingFiltersActionKind,
  sortingReducer,
} from '../components/Titling/utils';
import { PermissionEnum } from '../constants/permissions';
import { FiltersActionKind as GlobalFiltersActionKind } from '../globalFiltersUtils';
import usePaginationControls from '../hooks/usePaginationControls';
import { useUserAbilityAndVisibleStates } from '../hooks/useUserAbilityAndVisibleStates';
import { FiltersContext } from '../libs/contextLib';
import { User } from '../types/user';
import { isNullOrUndefined } from '../utils';
import { Swimlane, allSwimlanes, groupDealsByState } from '../utils/dashboard';
import { filterOwner, isAllSources, isAllTypes } from '../utils/permissions';
import {
  filterDeletedTags,
  mergeCreatedTags,
  updateDealTags,
  updateSwimlanesDealTags,
} from '../utils/tags';

type MergeDealArgs = {
  originalDeals: Deal[];
  updatedDeals: Deal[];
  user: User;
  userPodIds: number[];
  userFsPodIds: number[];
  userPermissions: Set<PermissionEnum>;
  options: {
    filterByOwner: boolean;
    seeAllPods: boolean;
    seeAllFsPods: boolean;
    showScheduledFollowUps: boolean;
    fsLocation?: string;
  };
};

const mergeDeals = ({
  originalDeals,
  updatedDeals,
  user,
  userPodIds,
  userFsPodIds,
  userPermissions,
  options: { filterByOwner, seeAllPods, seeAllFsPods, showScheduledFollowUps, fsLocation },
}: MergeDealArgs) => {
  let filterOriginalDeals = [...originalDeals];
  let filterUpdatedDeals = [...updatedDeals];

  const updatedDealIds: number[] = filterUpdatedDeals.map((ud) => ud.id as number);

  filterOriginalDeals = filterOriginalDeals.filter(
    (od) => !updatedDealIds.includes(od.id as number),
  );

  if (filterByOwner) {
    filterUpdatedDeals = filterOwner(filterUpdatedDeals, user, userPermissions);
  }

  // Filter for when onDealUpdate was for followUp or pod/user assignment
  filterUpdatedDeals = filterUpdatedDeals.filter((deal) => {
    const dealHasSalesPodAssigned = !!deal.pod?.id;
    const userBelongsToAssignedSalesPod = userPodIds.includes(deal.pod?.id ?? 0);
    const dealHasFsPodAssigned = !!deal.fs_pod?.id;
    const userBelongsToAssignedFsPod = userFsPodIds.includes(Number(deal.fs_pod?.id ?? 0));
    const dealHasDueFollowUp = deal.follow_up?.status === FollowUpStatusEnum.Due;
    const dealDoesNotHaveFollowUp = !deal.follow_up;

    // Check location filter
    const locationMatches = fsLocation ? deal.closer?.location === fsLocation : true;

    // Apply all filters together
    return (
      locationMatches &&
      (seeAllPods || !dealHasSalesPodAssigned || userBelongsToAssignedSalesPod) &&
      (seeAllFsPods || !dealHasFsPodAssigned || userBelongsToAssignedFsPod) &&
      (showScheduledFollowUps || dealDoesNotHaveFollowUp || dealHasDueFollowUp)
    );
  });

  // Return all relevant deals
  return filterUpdatedDeals.concat(filterOriginalDeals);
};

export enum CurrentView {
  DashboardView = 'DashboardView',
  TitleTableView = 'TitleTableView',
  PayoffRequestsView = 'PayoffRequestsView',
}

const Dashboard = () => {
  const {
    filters: {
      titling: titlingFilters,
      global: {
        selectedPodIds,
        selectedFsPodIds,
        sources,
        types,
        currentView,
        showOnlyUnread,
        showAssignedToMe,
        showScheduledFollowUps,
        showBootRequested,
        showAssignedToDeletedUsers,
        showActiveTags,
        showMatureFollowUps,
        location,
      },
    },
    dispatch: dispatchFilters,
  } = useContext(FiltersContext);

  const [isLargerThan2008] = useMediaQuery('(min-width: 2008px)');

  const {
    viewAsUser,
    viewAsUserAbility,
    viewAsUserStates,
    viewAsUserPods,
    loggedUser,
    loggedUserAbility,
    loggedUserStates,
    loading: loadingViewAsUserAbilityAndVisibleStates,
    viewingAsSelf,
  } = useUserAbilityAndVisibleStates();

  const [sorting, dispatchSorting] = useReducer(sortingReducer, {
    field: 'deal_dates.custom_dates.signed',
    direction: SortDirection.Asc,
  });

  const paginationControls = usePaginationControls({
    initialCurrentPage: titlingFilters.currentPage,
    initialItemsPerPage: titlingFilters.itemsPerPage,
  });

  useEffect(() => {
    if (viewAsUser.id !== loggedUser.id) {
      dispatchFilters({
        type: GlobalFiltersActionKind.SET_CURRENT_VIEW,
        payload: CurrentView.DashboardView,
      });
    }
  }, [viewAsUser.id, loggedUser.id]);

  useEffect(() => {
    dispatchFilters({
      type: TitlingFiltersActionKind.SET_NOTIFICATIONS,
      payload: showOnlyUnread,
    });
  }, [showOnlyUnread]);

  useEffect(() => {
    if (titlingFilters.field === sorting.field && titlingFilters.direction === sorting.direction) {
      return;
    }

    dispatchSorting({
      type: SortingActionKind.SET_FIELD,
      payload: titlingFilters.field,
    });
    dispatchSorting({
      type: SortingActionKind.SET_DIRECTION,
      payload: titlingFilters.direction,
    });
  }, [
    titlingFilters.field,
    titlingFilters.direction,
    titlingFilters.currentPage,
    titlingFilters.itemsPerPage,
    sorting.field,
    sorting.direction,
  ]);

  const {
    data: dashboardDealsData,
    loading: dashboardDealsLoading,
    refetch,
  } = useDashboardDealsQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      loadAll: false,
      assignedToMeFilter: showAssignedToMe,
      bootRequestedFilter: showBootRequested,
      assignedToDeletedUsersFilter: showAssignedToDeletedUsers,
      unreadMessagesFilter: showOnlyUnread,
      // If we have all sources, we don't need to pass them to the query, no need to filter by source
      sources: isAllSources(sources) ? [] : sources,
      types: isAllTypes(types) ? [] : types,
      states: viewAsUserStates,
      showScheduledFollowUps,
      podIds: selectedPodIds,
      fsPodIds: selectedFsPodIds,
      // Only pass these values if you aren't viewing as yourself, otherwise they'll be on the token already
      ...(!viewingAsSelf && {
        userId: viewAsUser?.id,
        permissions: Array.from(viewAsUserAbility),
      }),
      showActiveTags,
      showMatureFollowUps,
      closerLocation: location ? (location as LocationType) : undefined,
    },
    skip:
      currentView !== CurrentView.DashboardView ||
      !viewAsUserStates.length ||
      loadingViewAsUserAbilityAndVisibleStates,
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // It would be nice to remove this `useState` but it is a mess
  // to handle the `onDealUpdate` subscription without it
  const [swimlanes, setSwimlanes] = useState<Swimlane>({} as Swimlane);
  const [selectedTitlingTabIndex, setSelectedTitlingTabIndex] = useState(
    titlingFilters.selectedTitlingTab,
  );

  useEffect(() => {
    if (titlingFilters.selectedTitlingTab !== selectedTitlingTabIndex) {
      dispatchFilters({
        type: TitlingFiltersActionKind.SET_SELECTED_TITLING_TAB,
        payload: selectedTitlingTabIndex,
      });
    }
  }, [titlingFilters.selectedTitlingTab, selectedTitlingTabIndex]);

  useEffect(() => {
    if (!dashboardDealsData?.dashboardDeals?.deals?.length) {
      setSwimlanes({} as Swimlane);
      return;
    }
    // TODO: temporary conversion. We should remove all classes and use the generated classes
    setSwimlanes(groupDealsByState(dashboardDealsData.dashboardDeals.deals as Deal[]));
  }, [dashboardDealsData?.dashboardDeals?.deals]);

  const { data } = useOnDealUpdateSubscription({
    variables: { sources, types },
  });
  // TODO: temporary conversion. We should remove all classes and use the generated classes
  const onDealUpdateData = data as { onDealUpdate?: Deal[] };

  useEffect(() => {
    const updatedDeals = onDealUpdateData?.onDealUpdate;
    if (!updatedDeals) {
      return;
    }

    // Using the functional approach to avoid race condition with tags created/deleted subscriptions.
    setSwimlanes((prev) => {
      // Merges updated deals with the original deals after applying filters
      const mergedDeals = mergeDeals({
        originalDeals: Object.keys(prev).flatMap((key) => prev[key as DealStateEnum]),
        updatedDeals,
        user: viewAsUser,
        userPodIds: selectedPodIds?.length
          ? selectedPodIds.filter((id) => !isNullOrUndefined(id))
          : viewAsUserPods
              .filter((pod) => pod?.team_type !== TeamType.FinancialSpecialist)
              .map((pod) => pod?.id)
              .filter((id) => !isNullOrUndefined(id)),
        userFsPodIds: selectedFsPodIds?.length
          ? selectedFsPodIds.filter((id) => !isNullOrUndefined(id))
          : viewAsUserPods
              .filter((pod) => pod?.team_type === TeamType.FinancialSpecialist)
              .map((pod) => pod?.id)
              .filter((id) => !isNullOrUndefined(id)),
        userPermissions: viewAsUserAbility,
        options: {
          filterByOwner: viewAsUserAbility.has(PermissionEnum.FilterDealOwner),
          seeAllPods: viewAsUserAbility.has(PermissionEnum.SeeAllPods),
          seeAllFsPods: viewAsUserAbility.has(PermissionEnum.SeeAllFsPods),
          showScheduledFollowUps,
          fsLocation: location,
        },
      });

      return groupDealsByState(mergedDeals);
    });
  }, [onDealUpdateData?.onDealUpdate]);

  const { data: tagsCreatedData } = useOnTagsCreatedSubscription();
  const { data: tagsDeletedData } = useOnTagsDeletedSubscription();

  useEffect(() => {
    const tagsPayload = tagsCreatedData?.onTagsCreated;
    if (!tagsPayload) {
      return;
    }

    setSwimlanes((prev) =>
      updateSwimlanesDealTags({
        swimlane: prev,
        tagsPayload,
        updateFn: mergeCreatedTags,
      }),
    );
  }, [tagsCreatedData?.onTagsCreated]);

  useEffect(() => {
    const tagsPayload = tagsDeletedData?.onTagsDeleted;
    if (!tagsPayload) {
      return;
    }

    setSwimlanes((prev) =>
      updateSwimlanesDealTags({
        swimlane: prev,
        tagsPayload,
        updateFn: filterDeletedTags,
      }),
    );
  }, [tagsDeletedData?.onTagsDeleted]);

  const { data: laneKeyMetricsReaderData } = useLaneKeyMetricsReaderQuery({
    variables: {
      today: startOfToday(),
      sources,
      types,
    },
    fetchPolicy: 'cache-and-network',
    skip: currentView !== CurrentView.DashboardView,
  });

  const laneKeyMetricsReader = (laneKeyMetricsReaderData?.laneKeyMetricsReader ??
    []) as LaneKeyMetrics[];

  const skipTitlingDealsOperations = useMemo(
    () => currentView !== CurrentView.TitleTableView,
    [currentView],
  );
  // `selectedTitlingTab` is used to keep the selected tab when changing pages.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { currentPage, itemsPerPage, field, direction, selectedTitlingTab, ...filters } =
    titlingFilters;
  // Only used to keep 'Team' and 'Assigned to' filters when changing page.
  delete filters.userChangedFilters;
  // `Temp Tags Queue` only filters.
  delete filters.tempTagsQueueSortColumn;
  delete filters.tempTagsQueueSortDirection;
  const { data: titlingDashboardDealsData, loading: titlingDashboardDealsLoading } = useQuery<
    Pick<Query, 'titlingDashboardDeals'>
  >(titlingDashboardDealsQuery, {
    variables: {
      sources,
      types,
      assignedToDeletedUsersFilter: showAssignedToDeletedUsers,
      states: loggedUserStates,
      podIds: selectedPodIds,
      page: currentPage,
      itemsPerPage,
      filters,
      sorting: { field, direction },
      showActiveTags,
    },
    skip: skipTitlingDealsOperations || !loggedUserStates.length || selectedTitlingTabIndex !== 0,
    fetchPolicy: 'cache-and-network',
  });

  const dealsMaxAge = useMemo(
    () => titlingDashboardDealsData?.titlingDashboardDeals?.dealsMaxAge ?? undefined,
    [titlingDashboardDealsData?.titlingDashboardDeals?.dealsMaxAge],
  );
  const [titlingDeals, setTitlingDeals] = useState<Deal[]>([]);

  useEffect(() => {
    if (!titlingDashboardDealsData?.titlingDashboardDeals) {
      setTitlingDeals([]);
      paginationControls.setTotalRecords(0);
      return;
    }

    const { totalRecords, deals } = titlingDashboardDealsData.titlingDashboardDeals;
    paginationControls.setTotalRecords(totalRecords ?? 0);
    setTitlingDeals((deals ?? []) as Deal[]);
  }, [titlingDashboardDealsData?.titlingDashboardDeals]);

  useEffect(() => {
    if (!onDealUpdateData?.onDealUpdate || skipTitlingDealsOperations) {
      return;
    }

    setTitlingDeals((prev) => {
      const updated = [...prev];
      for (const updatedDeal of onDealUpdateData.onDealUpdate ?? []) {
        const dealIndex = updated.findIndex((d) => d.id === updatedDeal.id);
        if (dealIndex !== -1) {
          updated[dealIndex] = updatedDeal;
        }
      }
      return updated;
    });
  }, [onDealUpdateData?.onDealUpdate, skipTitlingDealsOperations]);

  useEffect(() => {
    if (!tagsCreatedData?.onTagsCreated || skipTitlingDealsOperations) {
      return;
    }

    setTitlingDeals((prev) =>
      updateDealTags({
        deals: prev,
        tagsPayload: tagsCreatedData?.onTagsCreated,
        updateFn: mergeCreatedTags,
      }),
    );
  }, [tagsCreatedData?.onTagsCreated, skipTitlingDealsOperations]);

  useEffect(() => {
    if (!tagsDeletedData?.onTagsDeleted || skipTitlingDealsOperations) {
      return;
    }

    setTitlingDeals((prev) =>
      updateDealTags({
        deals: prev,
        tagsPayload: tagsDeletedData?.onTagsDeleted,
        updateFn: filterDeletedTags,
      }),
    );
  }, [tagsDeletedData?.onTagsDeleted, skipTitlingDealsOperations]);

  const stateCounts = useMemo(() => {
    const sCounts = dashboardDealsData?.dashboardDeals?.stateCounts;
    const res: { [key: string]: number } = {};
    for (const sl of allSwimlanes) {
      if (!viewAsUserAbility.has((PermissionEnum.See + sl.title) as PermissionEnum)) {
        continue;
      }

      res[sl.title] = 0;
      for (const state of sl.states) {
        res[sl.title] += sCounts?.find((s) => s?.state === state)?.count ?? 0;
      }
    }
    return res;
  }, [dashboardDealsData?.dashboardDeals?.stateCounts]);

  const visibleSwimlanes = useMemo(() => {
    const res = [];
    for (const sl of allSwimlanes) {
      if (!viewAsUserAbility.has((PermissionEnum.See + sl.title) as PermissionEnum)) {
        continue;
      }

      let swimlaneDeals: Deal[] = [];
      for (const state of sl.states) {
        swimlaneDeals = swimlaneDeals.concat(swimlanes[state] || []);
      }

      const readerDailyCount = laneKeyMetricsReader.reduce((count, metric) => {
        if (!sl.states.includes(metric.state)) {
          return count;
        }
        return count.plus(metric.day_count ?? 0);
      }, new Big(0));

      res.push({
        title: sl.title,
        deals: swimlaneDeals,
        readerDailyCount: readerDailyCount.toNumber(),
        assumedId: viewAsUser.id,
      });
    }
    return res;
  }, [viewAsUserAbility, viewAsUser, swimlanes, laneKeyMetricsReader]);

  return (
    <Flex flexDirection="column">
      <ButtonsRow />
      <LeaseEndContainer
        isLoading={dashboardDealsLoading || loadingViewAsUserAbilityAndVisibleStates}
      >
        <Box bgColor="snowyWhite">
          {(loggedUserAbility.has(PermissionEnum.TitleClerk) ||
            loggedUserAbility.has(PermissionEnum.SuperUser)) &&
          currentView === CurrentView.TitleTableView ? (
            <>
              <Tabs
                index={selectedTitlingTabIndex}
                onChange={(index) => setSelectedTitlingTabIndex(index)}
                variant="enclosed"
              >
                <TabList borderBottom="1px">
                  {/* TODO: Create a variant for Tabs for consistency */}
                  <Tab
                    bgColor={selectedTitlingTabIndex === 0 ? 'white' : 'gray.200'}
                    borderRadius="8px 8px 0 0"
                    fontWeight="bold"
                    ml={1}
                    px={4}
                  >
                    Title Tracker
                  </Tab>
                  <Tab
                    bgColor={selectedTitlingTabIndex === 1 ? 'white' : 'gray.200'}
                    borderRadius="8px 8px 0 0"
                    fontWeight="bold"
                    ml={1}
                    px={4}
                  >
                    Temp Tags
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <TitleTable
                      deals={titlingDeals}
                      maxAge={dealsMaxAge}
                      loading={titlingDashboardDealsLoading}
                      dispatchSorting={dispatchSorting}
                      dispatchFilters={dispatchFilters}
                      paginationControls={paginationControls}
                    />
                  </TabPanel>
                  <TabPanel>
                    <TempTagsQueue skip={selectedTitlingTabIndex !== 1} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
              <Stack direction="column">
                <Flex justify="center">
                  <BottomSection minW="70%" />
                </Flex>
              </Stack>
            </>
          ) : loggedUserAbility.has(PermissionEnum.SeeAllPayoffRequests) &&
            currentView === CurrentView.PayoffRequestsView ? (
            <PayoffDealsTable />
          ) : (
            <Flex flexWrap="wrap" justifyContent="center" gap={4}>
              <Stack direction="column" w={isLargerThan2008 ? '70%' : '100%'}>
                <Flex gap={2} overflow="auto">
                  {visibleSwimlanes.map((sl) => (
                    <DealsSwimlane
                      key={sl.title}
                      deals={sl.deals}
                      title={sl.title}
                      readerDailyCount={sl.readerDailyCount}
                      assumedId={viewAsUser.id}
                      swimlaneCount={stateCounts[sl.title]}
                    />
                  ))}
                </Flex>

                <Flex w="100%" justifyContent="center">
                  {dashboardDealsData?.dashboardDeals?.hasMore && (
                    <Button
                      variant="secondary"
                      maxW="200px"
                      alignSelf="center"
                      onClick={() => refetch({ loadAll: true })}
                      isLoading={dashboardDealsLoading}
                    >
                      Load More Deals
                    </Button>
                  )}
                </Flex>
              </Stack>
              <BottomSection minW={{ base: '100%', md: 'unset' }} />
            </Flex>
          )}
        </Box>
      </LeaseEndContainer>
    </Flex>
  );
};

export default Dashboard;
